import { useState, useEffect, useCallback } from 'react';
import { APP_CATALOG, type AppDefinition, type AppStatus } from '../data/apps';
import { fetchAppCatalog, type KeycloakClient } from '../api/keycloak-admin';
import { useAuth } from '../auth/AuthProvider';

/** IDs of statically-defined apps (avoid duplicates) */
const STATIC_APP_IDS = new Set(APP_CATALOG.map(a => a.id));

/** Check whether a Keycloak client is marked hidden via vertex.hidden attribute */
function isHiddenClient(client: KeycloakClient): boolean {
  return (client.attributes?.['vertex.hidden'] ?? '') === 'true';
}

/** Convert a Keycloak client to an AppDefinition */
function clientToApp(client: KeycloakClient): AppDefinition {
  const attrs = client.attributes ?? {};
  const status: AppStatus = client.enabled
    ? (attrs['vertex.status'] as AppStatus) ?? 'online'
    : 'offline';

  return {
    id: client.clientId,
    name: client.name || client.clientId,
    description: client.description ?? '',
    icon: attrs['vertex.icon'] ?? '',
    iconDark: attrs['vertex.icon.dark'] ?? '',
    launchUrl: client.rootUrl || client.baseUrl || (client.redirectUris?.[0] ?? '#').replace('/*', ''),
    requiredRole: `app:${client.clientId}`,
    category: attrs['vertex.category'] ?? 'Uncategorized',
    status,
    team: attrs['vertex.team'] ?? undefined,
    hidden: isHiddenClient(client),
  };
}

interface UseAppCatalogResult {
  apps: AppDefinition[];
  loading: boolean;
  categories: string[];
  refresh: () => Promise<void>;
}

/** Merges static APP_CATALOG with dynamically-registered Keycloak clients */
export function useAppCatalog(): UseAppCatalogResult {
  const { token } = useAuth();
  const [dynamicApps, setDynamicApps] = useState<AppDefinition[]>([]);
  const [loading, setLoading] = useState(true);

  const loadClients = useCallback(async () => {
    try {
      setLoading(true);
      // The catalog endpoint already scopes results to what this user may see
      // (internal clients excluded server-side; hidden apps only for entitled
      // users), so we just drop statically-defined dupes and map the rest.
      const clients = await fetchAppCatalog(token);
      const appClients = clients.filter(c => !STATIC_APP_IDS.has(c.clientId));
      setDynamicApps(appClients.map(clientToApp));
    } catch {
      /* Silently fall back to static catalog */
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadClients(); }, [loadClients]);

  const apps = [...APP_CATALOG, ...dynamicApps];
  const categories = [...new Set(apps.map(a => a.category))];

  return { apps, loading, categories, refresh: loadClients };
}
